import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { getPendingInvitesForUser, acceptInvite } from '../services/inviteService';
import { hasMinimumRole as roleMeets } from '../services/roleHierarchy';

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const { user } = useAuth();
  const [toolAccess, setToolAccess] = useState({});
  const [roles, setRoles] = useState({});
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);

  useEffect(() => {
    if (!user) {
      setToolAccess({});
      setRoles({});
      setProfile(null);
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        // Fetch tool access
        const accessDoc = await getDoc(doc(db, 'Hugo_ToolAccess', user.uid));
        if (accessDoc.exists()) {
          const data = accessDoc.data();
          // Self-heal: docs created by server-side grants (e.g. beta invite
          // claims) may predate the tools field. MVP policy is every
          // authenticated user gets drift-accumulation, so backfill it.
          if (!data.tools?.['drift-accumulation']?.hasAccess) {
            const healedTools = {
              ...(data.tools || {}),
              'drift-accumulation': {
                hasAccess: true,
                grantedAt: Timestamp.now(),
                grantedBy: 'system_backfill',
              },
            };
            setToolAccess(healedTools);
            try {
              await setDoc(
                doc(db, 'Hugo_ToolAccess', user.uid),
                { tools: healedTools },
                { merge: true }
              );
            } catch (writeErr) {
              console.warn('Could not persist tool access backfill:', writeErr.message);
            }
          } else {
            setToolAccess(data.tools || {});
          }
          setRoles(data.roles || {});
        } else {
          // MVP auto-grant: give all authenticated users drift-accumulation access
          const defaultAccess = {
            tools: {
              'drift-accumulation': {
                hasAccess: true,
                grantedAt: Timestamp.now(),
                grantedBy: 'system',
              },
            },
          };
          // Set local state first, then try to persist
          setToolAccess(defaultAccess.tools);
          try {
            await setDoc(doc(db, 'Hugo_ToolAccess', user.uid), defaultAccess);
          } catch (writeErr) {
            // Firestore rules may block write, but access is still granted locally for MVP
            console.warn('Could not persist tool access to Firestore:', writeErr.message);
          }
        }

        setProfile({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
        });

        // Check for pending invites and auto-accept them
        try {
          if (user.email) {
            const invites = await getPendingInvitesForUser(user.email);
            if (invites.length > 0) {
              setPendingInvites(invites);
              // Auto-accept all pending invites
              for (const invite of invites) {
                try {
                  await acceptInvite(invite.id, invite, user.uid, user.email);
                } catch (inviteErr) {
                  console.warn('Could not accept invite:', inviteErr.message);
                }
              }
              // Refresh roles after accepting invites
              const refreshedDoc = await getDoc(doc(db, 'Hugo_ToolAccess', user.uid));
              if (refreshedDoc.exists()) {
                setRoles(refreshedDoc.data().roles || {});
              }
              setPendingInvites([]);
            }
          }
        } catch (inviteError) {
          console.warn('Could not check pending invites:', inviteError.message);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // MVP fallback: if Firestore is unavailable, grant default access locally
        setToolAccess({
          'drift-accumulation': {
            hasAccess: true,
            grantedAt: Timestamp.now(),
            grantedBy: 'system-fallback',
          },
        });
        setProfile({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const hasToolAccess = (toolSlug) => {
    return toolAccess[toolSlug]?.hasAccess === true;
  };

  const getUserRole = (organizationId) => {
    return roles[organizationId] || null;
  };

  const hasMinimumRole = (organizationId, requiredRole) => {
    const userRoleEntry = roles[organizationId];
    if (!userRoleEntry) return false;
    return roleMeets(userRoleEntry.role, requiredRole);
  };

  const value = {
    toolAccess,
    roles,
    profile,
    loading,
    pendingInvites,
    hasToolAccess,
    getUserRole,
    hasMinimumRole,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
